import { format } from "date-fns";
import type { Content, TableCell } from "pdfmake/interfaces";
import generatePDF from "helpers/generatePDF";
import { formatCurrency } from "helpers/utils";
import { listarItensEstoque } from "services/lojaEstoque";
import type { IEstoqueItemRow } from "interfaces/loja/estoque";

const ACCENT = "#F96030";

function statusLabel(estoque: number, limiteBaixo: number): string {
  if (estoque <= 0) return "Zerado";
  if (estoque <= limiteBaixo) return "Baixo";
  return "OK";
}

function statusColor(estoque: number, limiteBaixo: number): string | undefined {
  if (estoque <= 0) return "#c62828";
  if (estoque <= limiteBaixo) return "#e65100";
  return undefined;
}

function fmtPreco(valor: number | null): string {
  if (valor == null || !Number.isFinite(Number(valor))) return "—";
  return formatCurrency(valor);
}

export async function fetchItensEstoqueBaixoParaExport(
  limiteBaixo: number
): Promise<IEstoqueItemRow[]> {
  const items: IEstoqueItemRow[] = [];
  let page = 1;
  const limit = 100;

  while (true) {
    const { data, status } = await listarItensEstoque({ page, limit, limiteBaixo });
    if (status !== 200) {
      throw new Error("Não foi possível carregar os itens de estoque.");
    }

    for (const row of data.data) {
      if (row.estoque <= limiteBaixo) items.push(row);
    }

    if (page >= data.meta.totalPages) break;
    page += 1;
  }

  return items.sort((a, b) => a.estoque - b.estoque);
}

export async function exportEstoqueBaixoPdf(limiteBaixo: number): Promise<number> {
  const itens = await fetchItensEstoqueBaixoParaExport(limiteBaixo);

  if (itens.length === 0) {
    throw new Error(
      `Nenhum produto com estoque baixo ou zerado (limite ≤ ${limiteBaixo} un.).`
    );
  }

  const zerados = itens.filter((i) => i.estoque <= 0).length;
  const baixos = itens.filter((i) => i.estoque > 0 && i.estoque <= limiteBaixo).length;
  const emitidoEm = format(new Date(), "dd/MM/yyyy HH:mm");

  const headerRow: TableCell[] = [
    { text: "#", bold: true, fillColor: ACCENT, color: "#ffffff", fontSize: 8, alignment: "center" },
    { text: "SKU", bold: true, fillColor: ACCENT, color: "#ffffff", fontSize: 8 },
    { text: "Produto", bold: true, fillColor: ACCENT, color: "#ffffff", fontSize: 8 },
    { text: "Variação", bold: true, fillColor: ACCENT, color: "#ffffff", fontSize: 8 },
    { text: "Qtd", bold: true, fillColor: ACCENT, color: "#ffffff", fontSize: 8, alignment: "right" },
    { text: "Status", bold: true, fillColor: ACCENT, color: "#ffffff", fontSize: 8, alignment: "center" },
    { text: "Custo", bold: true, fillColor: ACCENT, color: "#ffffff", fontSize: 8, alignment: "right" },
    { text: "Venda", bold: true, fillColor: ACCENT, color: "#ffffff", fontSize: 8, alignment: "right" },
  ];

  const bodyRows: TableCell[][] = itens.map((item, index) => {
    const status = statusLabel(item.estoque, limiteBaixo);
    const color = statusColor(item.estoque, limiteBaixo);

    return [
      { text: String(index + 1), fontSize: 8, alignment: "center" },
      { text: item.sku, fontSize: 8 },
      { text: item.produtoNome, fontSize: 8 },
      { text: item.rotuloVariacao ?? "—", fontSize: 8 },
      { text: String(item.estoque), fontSize: 8, alignment: "right", bold: true, color },
      { text: status, fontSize: 8, alignment: "center", bold: true, color },
      { text: fmtPreco(item.precoCusto), fontSize: 8, alignment: "right" },
      { text: fmtPreco(item.precoVenda), fontSize: 8, alignment: "right" },
    ];
  });

  const resumoBody: TableCell[][] = [
    [
      { text: "Indicador", bold: true, fillColor: "#eeeeee", fontSize: 9 },
      { text: "Valor", bold: true, fillColor: "#eeeeee", fontSize: 9, alignment: "right" },
    ],
    [
      { text: "Limite de alerta (un.)", fontSize: 9 },
      { text: String(limiteBaixo), fontSize: 9, alignment: "right" },
    ],
    [
      { text: "Total de SKUs no relatório", fontSize: 9 },
      { text: String(itens.length), fontSize: 9, alignment: "right", bold: true },
    ],
    [
      { text: "Itens em baixa (1 a limite)", fontSize: 9 },
      { text: String(baixos), fontSize: 9, alignment: "right", color: "#e65100" },
    ],
    [
      { text: "Ruptura (zero)", fontSize: 9 },
      { text: String(zerados), fontSize: 9, alignment: "right", color: "#c62828" },
    ],
  ];

  const content: Content[] = [
    {
      text: "Relatório de estoque baixo",
      fontSize: 16,
      bold: true,
      alignment: "center",
      margin: [0, 0, 0, 4],
    },
    {
      text: `Produtos com quantidade ≤ ${limiteBaixo} unidades (inclui ruptura)`,
      fontSize: 10,
      alignment: "center",
      color: "#555555",
      margin: [0, 0, 0, 2],
    },
    {
      text: `Emitido em ${emitidoEm}`,
      fontSize: 8,
      alignment: "center",
      color: "#888888",
      margin: [0, 0, 0, 16],
    },
    {
      text: "Resumo",
      fontSize: 12,
      bold: true,
      margin: [0, 0, 0, 6],
    },
    {
      table: { widths: ["*", 80], body: resumoBody },
      layout: "lightHorizontalLines",
      margin: [0, 0, 0, 16],
    },
    {
      text: "Itens",
      fontSize: 12,
      bold: true,
      margin: [0, 0, 0, 6],
    },
    {
      table: {
        widths: [22, 70, "*", 70, 30, 42, 52, 52],
        headerRows: 1,
        body: [headerRow, ...bodyRows],
      },
      layout: "lightHorizontalLines",
    },
  ];

  await generatePDF({
    filename: `estoque-baixo-${format(new Date(), "yyyy-MM-dd")}`,
    content,
    footer: (currentPage, pageCount) => ({
      text: `Página ${currentPage} de ${pageCount}`,
      alignment: "center",
      fontSize: 8,
      color: "#888888",
      margin: [0, 8, 0, 0],
    }),
  });

  return itens.length;
}
